<template>
  <div v-if="noExpansion">
    <table
      v-if="normalizedBook"
      class="book-details-table"
    >
      <tr v-if="normalizedBook.title">
        <td>{{ $t("bookInfo.title") }}</td>
        <td>{{ decodeHtml(normalizedBook.title) }}</td>
      </tr>
      <tr v-if="normalizedBook.publishedOn || normalizedBook.modified || normalizedBook.createdAt">
        <td>{{ $t("bookInfo.published") }}</td>
        <td>{{ publicationYear(normalizedBook.publishedOn || normalizedBook.modified || normalizedBook.createdAt) }}</td>
      </tr>
      <tr v-if="normalizedBook.publisher">
        <td>{{ $t("bookInfo.publisher") }}</td>
        <td>
          <router-link
            v-if="normalizedBook.publisher.slug"
            :to="{ name: 'publisher', params: { slug: normalizedBook.publisher.slug } }"
            class="publisher-link"
          >
            {{ normalizedBook.publisher.name }}
          </router-link>
          <span v-else>{{ normalizedBook.publisher.name }}</span>
        </td>
      </tr>
      <tr v-if="normalizedBook.language">
        <td>{{ $t("bookInfo.language") }}</td>
        <td>{{ localizedLanguage }}</td>
      </tr>
      <tr v-if="normalizedBook.format">
        <td>{{ $t("bookInfo.format") }}</td>
        <td>{{ formattedAndTranslated }}</td>
      </tr>
      <tr v-if="normalizedBook.isbn">
        <td>{{ $t("bookInfo.isbn") }}</td>
        <td>{{ normalizedBook.isbn }}</td>
      </tr>
    </table>
  </div>
  <EkitabuExpansionPanelDeprecated
    v-else
    accordion
    gradient
    :value="0"
    :title="$t('bookInfo.bookDetails')"
  >
    <table
      v-if="normalizedBook"
      class="book-details-table"
    >
      <tr v-if="normalizedBook.title">
        <td>{{ $t("bookInfo.title") }}</td>
        <td>{{ decodeHtml(normalizedBook.title) }}</td>
      </tr>
      <tr v-if="normalizedBook.publishedOn || normalizedBook.modified || normalizedBook.createdAt">
        <td>{{ $t("bookInfo.published") }}</td>
        <td>{{ publicationYear(normalizedBook.publishedOn || normalizedBook.modified || normalizedBook.createdAt) }}</td>
      </tr>
      <tr v-if="normalizedBook.publisher">
        <td>{{ $t("bookInfo.publisher") }}</td>
        <td>
          <router-link
            v-if="normalizedBook.publisher.slug"
            :to="{ name: 'publisher', params: { slug: normalizedBook.publisher.slug } }"
            class="publisher-link"
          >
            {{ normalizedBook.publisher.name }}
          </router-link>
          <span v-else>{{ normalizedBook.publisher.name }}</span>
        </td>
      </tr>
      <tr v-if="normalizedBook.language">
        <td>{{ $t("bookInfo.language") }}</td>
        <td>{{ localizedLanguage }}</td>
      </tr>
      <tr v-if="normalizedBook.format">
        <td>{{ $t("bookInfo.format") }}</td>
        <td>{{ formattedAndTranslated }}</td>
      </tr>
      <tr v-if="normalizedBook.isbn">
        <td>{{ $t("bookInfo.isbn") }}</td>
        <td>{{ normalizedBook.isbn }}</td>
      </tr>
    </table>
  </EkitabuExpansionPanelDeprecated>
</template>

<script>
import { decodeHtml } from "@/helpers/generalFunctions";
import EkitabuExpansionPanelDeprecated from "@/components/EkitabuExpansionPanelDeprecated.vue";
import { isBook } from '@/types/books';
import { convertToLocalBook } from '@/utils/bookConverter';
import DateTimeMixin from "@/mixins/DateTimeMixin";

export default {
  name: "BookDetails",
  components: {
    EkitabuExpansionPanelDeprecated,
  },
  mixins: [DateTimeMixin],
  props: {
    book: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    noExpansion: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    currentLocale() {
      return this.$store.state.locale.languageCode || 'en';
    },
    localizedLanguage() {
      if (!this.normalizedBook.language) return '';
      try {
        const displayNames = new Intl.DisplayNames([this.currentLocale], {
          type: 'language'
        });
        return displayNames.of(this.normalizedBook.language);
      } catch (e) {
        console.warn('Language localization failed:', e);
        return this.normalizedBook.language.toUpperCase();
      }
    },
    normalizedBook() {
      return isBook(this.book) ? convertToLocalBook(this.book) : this.book;
    },
    formattedAndTranslated() {
      const format = this.normalizedBook.format;
      const formatId = format?._id || '';
      const cleanFormat = formatId.replace("application/", "");
      const translationKey = `formats.${cleanFormat}`;
      return this.$te(translationKey) ? this.$t(translationKey) : cleanFormat;
    }
  },
  methods: {
    decodeHtml(html) {
      return decodeHtml(html);
    },
  },
};
</script>

<style scoped lang="scss">
table {
  border: none;
  border-collapse: collapse;
}

.book-details-table tr td {
  padding: 7px 14px;
  font-size: 12px;
  line-height: normal;
}

.book-details-table tr:first-of-type td{ 
  padding-top: 14px;
}

.book-details-table tr:last-of-type td {
  padding-bottom: 14px;
}

.book-details-table tr td:first-child {
  color: var(--v-quaternaryText-base);
  font-weight: bold;
  padding-right: 30px;
  border-right: 2px solid var(--v-lightAccent-base);
}

.book-details-table tr td:last-of-type {
  word-break: break-word;
  padding-left: 30px;
}

.publisher-link {
  color: var(--v-primary-base);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}
</style>
